/** biome-ignore-all lint/style/noNestedTernary: allow that ternary! */
import cx from 'classnames';
import {memo, useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import Skeleton from 'react-loading-skeleton';
import {useSelector} from 'react-redux';
import {Route, useHistory, useRouteMatch} from 'react-router-dom';
import {ErrorBoundary} from '~/common/components/ErrorBoundary/ErrorBoundary';
import {selectClassifiedCategory} from '~/common/fabric/reducers/eviteEvent/selectors';
import {pluralize} from '~/common/utils';
import {FUNERAL_CATEGORY} from '~/common/utils/event_constants';
import {doesMatchEventCategory} from '~/common/utils/event_utils';
import {useGetGuestsQuery} from '~/ViewInvitation/api/endpoints/guests';
import {Avatar, AvatarPreview} from '~/ViewInvitation/components/Guestlist/Avatar';
import {GuestlistDataContextProvider} from '~/ViewInvitation/components/Guestlist/context/GuestlistDataContext';
import {GuestlistOverlay} from '~/ViewInvitation/components/Guestlist/GuestlistOverlay';
import {useCurrentGuestId} from '~/ViewInvitation/components/Guestlist/hooks/useCurrentGuestId';
import {useGuestlistDataContext} from '~/ViewInvitation/components/Guestlist/hooks/useGuestlistDataContext';
import {selectEventId} from '~/ViewInvitation/selectors/eventDetails';
import {selectIsFuneral} from '~/ViewInvitation/selectors/eviteEvent';
import {selectHasRsvpd} from '~/ViewInvitation/selectors/rsvp';
import {isPreview} from '~/ViewInvitation/utils';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {LockLineIcon} from '~library/svg/LockLineIcon';

import 'rc-tooltip/assets/bootstrap.css';
import * as styles from './WhosComingModule.module.scss';

const CreateFlowPreviewState = memo(() => {
  return (
    <div className={styles.preview}>
      <Typography variant="header7">Who's going</Typography>
      <div className={styles.avatars}>
        {Array.from({length: 30}).map((_, idx) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: idx as key is ok here
          <AvatarPreview key={idx} />
        ))}
      </div>
    </div>
  );
});

CreateFlowPreviewState.displayName = 'CreateFlowPreviewState';

const GuestlistLocked = memo(() => {
  return (
    <div className={styles.locked}>
      <div className={styles.lockedTopContent}>
        <LockLineIcon ratio={1} />
        <Typography variant="paragraph2" semibold>
          Want to view the guest list?
        </Typography>
      </div>
      <Typography variant="paragraph2">RSVP to see who’s going.</Typography>
    </div>
  );
});

GuestlistLocked.displayName = 'GuestlistLocked';

interface GuestlistPreviewProps {
  openOverlay: (guestId?: string) => void;
}

const GuestlistPreview = memo(({openOverlay}: GuestlistPreviewProps) => {
  const {
    state: {all: guestlist = [], summary},
  } = useGuestlistDataContext();

  const yesGuests = guestlist
    .filter((guest) => guest.rsvpResponse === 'yes')
    .slice(0, 20)
    .sort((a, b) => a?.name.localeCompare(b?.name));

  return (
    <div className={styles.guests}>
      <Typography variant="header7" size="large" className={styles.guestCount}>
        {pluralize('guest', summary?.yes)} going
      </Typography>
      {guestlist.length > 0 && (
        <div className={styles.avatars}>
          {yesGuests.map((guest) => (
            <Avatar key={guest.userId} guest={guest} onClick={openOverlay} hideBadge showShadow />
          ))}
        </div>
      )}
    </div>
  );
});

GuestlistPreview.displayName = 'GuestlistPreview';

const loadingSkeletons = (
  <div className={styles.guests}>
    <Skeleton height={30} />
    <Skeleton height={50} />
  </div>
);

interface WhosComingProps {
  isCreationFlow: boolean;
  isPreview: boolean;
}

const WhosComing = memo(({isPreview: isPreviewProp, isCreationFlow}: WhosComingProps) => {
  const history = useHistory();
  const isFuneralType = useSelector(selectIsFuneral);
  const hasRSVPd = useSelector(selectHasRsvpd);
  const initialHasRsvpd = useRef<boolean>(hasRSVPd);
  const eventId = useSelector(selectEventId);
  const {guestId} = useCurrentGuestId();
  const {refetch, isUninitialized} = useGetGuestsQuery(
    {
      eventId,
      guestId,
    },
    {skip: !guestId}
  );
  const classifiedCategory = useSelector(selectClassifiedCategory);
  const isFuneral = isFuneralType || doesMatchEventCategory(classifiedCategory, FUNERAL_CATEGORY);
  const {
    state: {error, isLoading, summary},
  } = useGuestlistDataContext();
  const hasNotRsvpd = summary.lockedStatus === 'no_rsvp';
  const guestsRoute = !!useRouteMatch(`/guests`);
  const [overlayOpen, setOverlayOpen] = useState(guestsRoute);

  useEffect(() => {
    if (initialHasRsvpd.current !== hasRSVPd && !isUninitialized) {
      refetch();
    }
  }, [hasRSVPd, refetch, isUninitialized]);

  useEffect(() => {
    setOverlayOpen(guestsRoute);
  }, [guestsRoute]);

  // TODO: remove setTimeouts in favor of properly waiting for the slide animations
  const openOverlay = useCallback(() => {
    history.push({
      pathname: '/guests',
      search: window.location.search,
    });
    setTimeout(() => setOverlayOpen(true), 200);
  }, [history]);

  const closeOverlay = useCallback(() => {
    setOverlayOpen(false);
    history.replace({pathname: '/', search: window.location.search});
  }, [history]);

  const onClick = useCallback(() => openOverlay(), [openOverlay]);

  const isPreviewState = isPreviewProp || isPreview();
  const isLockedState = !guestId || (!isFuneral && hasNotRsvpd);
  const isErrorOrLockedState = !!error || isLockedState;
  const isNormalState = (!isLockedState && !error) || isCreationFlow;

  return (
    <ErrorBoundary
      componentName="WhosComingModule"
      fallback={() => (
        <Typography variant="list1" color="charcoal">
          We couldn't load Guest List, try refreshing the page.
        </Typography>
      )}
    >
      <div className={styles.container}>
        <div className={styles.titleWrapper}>
          <Typography variant="label2" color="pebble" data-qa-id="who-is-coming-module-title">
            Guest List
          </Typography>
          {!isLoading && isNormalState && !isCreationFlow && (
            <Button
              variant="link"
              data-qa-id="see-all-guests"
              onClick={onClick}
              disabled={isPreviewState}
              type="button"
            >
              View All
            </Button>
          )}
        </div>
        {isLoading && loadingSkeletons}
        {!isLoading && (
          <div
            className={cx(styles.content, {
              [styles.contentPreview]: isCreationFlow || isErrorOrLockedState,
            })}
          >
            {isCreationFlow ? (
              <CreateFlowPreviewState />
            ) : isErrorOrLockedState ? (
              <GuestlistLocked />
            ) : isNormalState ? (
              <GuestlistPreview openOverlay={openOverlay} />
            ) : null}
          </div>
        )}
        <Route path="/guests">
          {isNormalState &&
            createPortal(
              <GuestlistOverlay isOpen={overlayOpen} onClose={closeOverlay} />,
              document.body
            )}
        </Route>
      </div>
    </ErrorBoundary>
  );
});

WhosComing.displayName = 'WhosComing';

export const WhosComingModule = memo(({isPreview, isCreationFlow}: WhosComingProps) => (
  <GuestlistDataContextProvider>
    <WhosComing isPreview={isPreview} isCreationFlow={isCreationFlow} />
  </GuestlistDataContextProvider>
));

WhosComingModule.displayName = 'WhosComingModule';
